import React, { useEffect, useState } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import awsConfig from './aws-exports';
import { jwtDecode } from 'jwt-decode';
import 'bootstrap/dist/css/bootstrap.min.css';
import './AdminStyles.css';
import DashboardLayout from './views/DashboardLayout';

Amplify.configure(awsConfig);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const log = (...args) => console.log('[WedArchive Admin]', ...args);

    const checkAuth = async () => {
      log('🔍 BEGIN: Auth flow check');

      try {
        const user = await Auth.currentAuthenticatedUser();
        log('✅ Found user without needing redirect:', user);
        setUser(user);
        setLoading(false);
      } catch (err) {
        log('❌ No current user found:', err);

        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        log('🔎 URL code param =', code);

        if (code) {
          log('📦 Code present. Running _handleAuthResponse...');
          try {
            await Auth._handleAuthResponse();

            const session = await Auth.currentSession();
            log('✅ Session object:', session);

            let user = await Auth.currentAuthenticatedUser();
            if (!user.signInUserSession) {
              log('⚠️ signInUserSession is null after session hydration, using fallback');
              const clientId = awsConfig.Auth.userPoolWebClientId;
              const usernameKey = Object.keys(localStorage).find(k =>
                k.startsWith(`CognitoIdentityServiceProvider.${clientId}`) && k.endsWith('.LastAuthUser')
              );
              const username = localStorage.getItem(usernameKey);
              const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`);
              if (idToken) {
                const decoded = jwtDecode(idToken);
                log('🧪 Fallback decoded token:', decoded);
                user = {
                  username: decoded['cognito:username'],
                  attributes: { email: decoded.email },
                  signInUserSession: {
                    idToken: { jwtToken: idToken }
                  }
                };
              }
            }

            log('✅ User after session hydration/fallback:', user);
            setUser(user);
            setLoading(false);
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch (handleErr) {
            log('❌ ERROR in _handleAuthResponse():', handleErr);
            setLoading(false);
          }
        } else {
          log('🔁 No code and no user. Redirecting to Google federated login...');
          await Auth.federatedSignIn({ provider: 'Google' });
        }
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return <div className="container mt-4">Loading...</div>;
  }

  if (!user || !user.signInUserSession || !user.signInUserSession.idToken) {
    return <div className="container mt-4">Redirecting to login...</div>;
  }

  // ✅ Enforce admin pool access
  const token = user.signInUserSession.idToken.jwtToken;
  const decoded = jwtDecode(token);
  const userPoolId = decoded.iss?.split('/').pop();
  const isValidAdmin = (userPoolId === 'us-west-1_d0LA3tosn' && decoded.email === 'jerdos@wedarchive.com');

  if (!isValidAdmin) {
    console.warn('🚫 Blocked user not in group:', user.username, 'Groups:', ["admin_pool_validation"]);

    return (
      <div className="container mt-4">
        <h2>🚫 Access Denied</h2>
        <p>You are not a member of the required admin group.</p>
        <button className="btn btn-danger mt-3" onClick={() => Auth.signOut()}>
          Sign Out
        </button>
      </div>
    );
  }

  console.log('✅ Rendering user:', user);

  return (
    <DashboardLayout
      user={user}
      onLogout={() => Auth.signOut()}
    />
  );
}

export default App;
